import type { KeyInitiative } from 'partner360/entities/keyInitiatives'

import type {
  KeyInitiativesReviewDevelopmentGroup,
  KeyInitiativesReviewStatusGroup,
  KeyInitiativesReviewSummary,
  KeyInitiativesReviewTotals,
} from '../model/types'

const UNSPECIFIED_LABEL = 'Not specified'
const H2_QUARTERS = new Set(['Q3', 'Q4'])

const normalizeQuarter = (value: string) => value.trim().toUpperCase()

const parsePeriod = (period: string | null | undefined) => [
  ...new Set((period ?? '').split(',').map(normalizeQuarter).filter(Boolean)),
]

const hasH2Quarter = (period: string | null | undefined) =>
  parsePeriod(period).some((quarter) => H2_QUARTERS.has(quarter))

const prorateH2Value = (value: number | null | undefined, period: string | null | undefined) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }

  const quarters = parsePeriod(period)

  if (quarters.length === 0) {
    return null
  }

  const h2QuarterCount = quarters.filter((quarter) => H2_QUARTERS.has(quarter)).length

  if (h2QuarterCount === 0) {
    return null
  }

  return (value / quarters.length) * h2QuarterCount
}

const getLabel = (value: string | null | undefined) => {
  const normalized = value?.trim()
  return normalized ? normalized : UNSPECIFIED_LABEL
}

const createTotals = (): KeyInitiativesReviewTotals => ({
  count: 0,
  goalNdp: null,
  goalNet: null,
  goalTNet: null,
})

const addNullable = (current: number | null, next: number | null) => {
  if (next === null) {
    return current
  }

  if (current === null) {
    return next
  }

  return current + next
}

const accumulateTotals = (totals: KeyInitiativesReviewTotals, item: KeyInitiative) => {
  totals.count += 1
  totals.goalNdp = addNullable(totals.goalNdp, prorateH2Value(item.GoalNdp, item.Period))
  totals.goalNet = addNullable(totals.goalNet, prorateH2Value(item.GoalNet, item.Period))
}

export const buildKeyInitiativesReviewSummary = (
  items: KeyInitiative[]
): KeyInitiativesReviewSummary => {
  const filteredItems = items.filter((item) => hasH2Quarter(item.Period))
  const totals = createTotals()
  const developmentMap = new Map<
    string,
    {
      totals: KeyInitiativesReviewTotals
      statuses: Map<string, KeyInitiativesReviewTotals>
    }
  >()

  filteredItems.forEach((item) => {
    const development = getLabel(item.Development)
    const status = getLabel(item.Status)
    const developmentEntry = developmentMap.get(development) ?? {
      totals: createTotals(),
      statuses: new Map<string, KeyInitiativesReviewTotals>(),
    }
    const statusTotals = developmentEntry.statuses.get(status) ?? createTotals()

    accumulateTotals(totals, item)
    accumulateTotals(developmentEntry.totals, item)
    accumulateTotals(statusTotals, item)

    developmentEntry.statuses.set(status, statusTotals)
    developmentMap.set(development, developmentEntry)
  })

  const groups = [...developmentMap.entries()]
    .map<KeyInitiativesReviewDevelopmentGroup>(([development, developmentEntry]) => ({
      development,
      totals: developmentEntry.totals,
      statuses: [...developmentEntry.statuses.entries()]
        .map<KeyInitiativesReviewStatusGroup>(([status, statusTotals]) => ({
          status,
          totals: statusTotals,
        }))
        .sort((left, right) => left.status.localeCompare(right.status)),
    }))
    .sort((left, right) => left.development.localeCompare(right.development))

  return {
    items: filteredItems,
    totals,
    groups,
  }
}
